import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@fluentui/react-components';

export default function FilePickButton({
  label,
  accept,
  disabled,
  busy,
  uploadedName,
  fileName,
  onPick,
  onRemove,
  removeLabel = 'Remove',
}: {
  label: string;
  accept?: string;
  disabled?: boolean;
  busy?: boolean;
  uploadedName?: string;
  /**
   * The file the parent holds for this slot, when the parent owns it (a file can also arrive by DROP, which
   * never passes through this input). Set, even to null, it is what the button names; unset, the button
   * remembers its own last pick.
   */
  fileName?: string | null;
  /** Return false to refuse the file (it failed up-front validation): its name is then not shown as chosen. */
  onPick: (file: File) => void | boolean;
  /** When set (and a file is on file), renders a remove (✕) button. */
  onRemove?: () => void;
  /** Accessible label for the remove button. */
  removeLabel?: string;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickedName, setPickedName] = useState<string | null>(null);

  // Once the parent confirms an on-file name (the upload/ingest completed and refetched),
  // drop the transient just-picked name so the ✓ on-file indicator + remove (✕) take over.
  // Without this, pickedName stays set for the whole session and hides the ✕.
  useEffect(() => {
    if (uploadedName) setPickedName(null);
  }, [uploadedName]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (onPick(file) !== false) setPickedName(file.name);
  }

  const shownPick = fileName !== undefined ? fileName : pickedName;
  let filenameText: string;
  if (shownPick) {
    filenameText = shownPick;
  } else if (uploadedName) {
    filenameText = `✓ ${uploadedName}`;
  } else {
    filenameText = t('upload.noFile');
  }

  return (
    <span>
      <Button
        size="small"
        appearance="secondary"
        disabled={busy || disabled}
        icon={busy ? <Spinner size="tiny" /> : undefined}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <span style={{ maxWidth: '22rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', display: 'inline-block' }} title={filenameText}>{filenameText}</span>
      {onRemove && !shownPick && uploadedName && (
        <Button
          size="small"
          appearance="secondary"
          aria-label={removeLabel}
          title={removeLabel}
          disabled={busy || disabled}
          onClick={onRemove}
        >
          ✕ {removeLabel}
        </Button>
      )}
    </span>
  );
}
